// GREAT EXAMPLE
// https://github.com/WebDevSimplified/React-Firebase-Auth

import {
    browserLocalPersistence,
    browserSessionPersistence,
    confirmPasswordReset,
    sendPasswordResetEmail,
    setPersistence,
    signInWithEmailAndPassword,
    validatePassword,
    verifyPasswordResetCode,
} from "firebase/auth";
import React, { createContext, useContext, useState } from "react";

import { getFirebaseAuth } from "../corvusFirebase.js";

export const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState();
    const [isLoading, setLoading] = useState(true);
    const [userClaims, setUserClaims] = useState();

    React.useEffect(() => {
        const auth = getFirebaseAuth();

        const unsubscribe = auth.onAuthStateChanged(async (newUser) => {
            setUser(newUser);

            if (newUser) {
                const idTokenResult = await newUser.getIdTokenResult();
                setUserClaims(idTokenResult.claims);
            } else {
                setUserClaims(undefined);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    async function login(email, password, keepSignedIn) {
        const auth = getFirebaseAuth();
        const persistenceMode = keepSignedIn
            ? browserLocalPersistence
            : browserSessionPersistence;

        await setPersistence(auth, persistenceMode);
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return getFirebaseAuth().signOut();
    }

    function resetPasswordLink(resetEmail) {
        return sendPasswordResetEmail(getFirebaseAuth(), resetEmail);
    }

    function corvusConfirmPasswordReset(code, newPassword) {
        return confirmPasswordReset(getFirebaseAuth(), code, newPassword);
    }

    const corvusVerifyPasswordResetCode = React.useCallback((code) => {
        return verifyPasswordResetCode(getFirebaseAuth(), code);
    }, []);

    const corvusValidatePassword = React.useCallback((password) => {
        return validatePassword(getFirebaseAuth(), password);
    }, []);

    const value = {
        user,
        isLoading,
        userClaims,
        setUserClaims,
        setUser,
        login,
        logout,
        resetPasswordLink,
        corvusConfirmPasswordReset,
        corvusVerifyPasswordResetCode,
        corvusValidatePassword,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};
